// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGetFulfillmentLabel } from "@vanmeter/App/Store/Selectors/ContextSelector";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
const enum fields {
    isNavigationDrawer = "isNavigationDrawer",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isNavigationDrawer]: boolean;
    };
}
// </XNG-Changes>

const mapStateToProps = (state: ApplicationState) => {
    // <XNG-Changes>
    const currentLocation = getLocation(state);
    const returnUrl = encodeURIComponent(`${currentLocation.pathname}${currentLocation.search}`);
    const changeCustomerPageUrl =
        (getPageLinkByPageType(state, "ChangeCustomerPage")?.url || "") + `?returnUrl=${returnUrl}`;
    // </XNG-Changes>
    const pageType = getCurrentPage(state).type;
    const isChangeCustomerPage = pageType === "ChangeCustomerPage";

    return {
        // XNG-Changes: use xGetFulfillmentLabel
        fulfillmentInfo: xGetFulfillmentLabel(state),
        isDrawerOpen: state.components.addressDrawer.isOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        // XNG-Changes
        changeCustomerPageUrl,
        isChangeCustomerPage,
    };
};

const mapDispatchToProps = {
    setDrawerIsOpen,
    // XNG-Changes
    setNavDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: LinkPresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    // XNG-Changes
    titleLabel?: TypographyPresentationProps;
    titleLabelWrapper?: InjectableCss;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: inline-flex;
            width: 100%;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
            width: calc(100% - 10px);
            padding: 6px 0;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    drawer: baseDrawerStyles,
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
};

const HeaderShipToAddress: FC<Props> = ({
    id,
    fulfillmentInfo,
    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    isPunchOutSession,
    // XNG-Changes
    changeCustomerPageUrl,
    fields: { isNavigationDrawer },
    setNavDrawerIsOpen,
    isChangeCustomerPage,
}) => {
    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setDrawerIsOpen({ isOpen: !isDrawerOpen });
    };

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
    };

    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);

    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    // XNG-Changes
    const closeDrawer = () => {
        if (!isNavigationDrawer) {
            return;
        }

        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    // XNG-Changes: update structure
    return (
        <>
            <StyledWrapper {...styles.shipToAddressWrapper}>
                <StyledWrapper {...styles.titleLabelWrapper} {...{ showBorder: !isPunchOutSession }}>
                    {/* XNG-Changes: add title */}
                    <Typography {...styles.titleLabel}>{translate(fulfillmentInfo.addressTitle)}:</Typography>

                    {/* As this doesn't look final, but is the pattern on which the mobile nav drawer was based,
                    please also make changes to `NavigationDrawer.tsx` when you change this. */}
                    {/* <Icon {...styles.titleIcon} src={MapPin} /> */}
                    <Typography
                        {...styles.titleTypography}
                        data-test-selector="header_shipToLabel"
                        {...{ isNavigationDrawer }}
                        title={fulfillmentInfo.addressLabel}
                    >
                        {fulfillmentInfo.addressLabel}
                    </Typography>
                </StyledWrapper>
                {/* XNG-Changes: use Link and redirect to changeCustomerPageUrl */}
                {!isPunchOutSession && (
                    <Link
                        href={isChangeCustomerPage ? "" : changeCustomerPageUrl}
                        {...styles.titleClickable}
                        data-test-selector="header_shipToButton"
                        // XNG-Changes
                        onClick={closeDrawer}
                        aria-label={translate("Change")}
                        disabled={isChangeCustomerPage}
                    >
                        {translate("Change")}
                    </Link>
                )}
            </StyledWrapper>
            {/* <Drawer
                {...drawerStyles}
                position="top"
                isOpen={isDrawerOpen}
                headline={isPunchOutSession ? "" : translate("Change Customer")}
                handleClose={handleCloseDrawer}
                data-test-selector="addressDrawer"
            >
                <Zone zoneName="Container" contentId={id} />
            </Drawer> */}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.isNavigationDrawer,
                displayName: "Is Navigation Drawer",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
